import { Box, Button, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';
import React, { startTransition, useCallback, useMemo, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import { ExpandIcon } from '../ExpandIcon';
import { ListBox } from './ListBox';

import { singleSelectStoreProps } from './types';

type Props<TFilterOptions extends string> = {
  label: TFilterOptions;
  options: { label: string; value: string; count?: number }[];
  selectedOption?: string;
  onChange: (selected: string) => void;
  isStickyBar?: boolean;
  scrollBackToMainBar?: () => void;
  useStore: singleSelectStoreProps<TFilterOptions>;
  placeholder?: string;
  isFiltersInHeader?: boolean;
};

export { ListBox } from './ListBox';
export { ListOptions } from './ListOptions';
export type { SingleSelectOption, singleSelectStoreProps } from './types';

export const SingleSelectDropdown = <TFilterOptions extends string>({
  label,
  options,
  selectedOption,
  onChange,
  isStickyBar = false,
  scrollBackToMainBar,
  useStore,
  placeholder,
  isFiltersInHeader,
}: Props<TFilterOptions>) => {
  const buttonBorderRadius = isFiltersInHeader ? '8px' : '64px';
  const buttonPaddingY = isFiltersInHeader ? 2 : 1;
  const handleOpenKey = isStickyBar ? 'stickyBar' : 'normalBar';
  const isOpen = useStore(state => state.openedFilters[handleOpenKey][label], shallow);

  const handleOpenedFilters = useStore(state => state.handleOpenedFilters, shallow);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isSelected = !!selectedOption;

  const [activeIndex, setActiveIndex] = React.useState(() => {
    const index = options.findIndex(option => option.value === selectedOption);
    return index === -1 ? 0 : index;
  });

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        startTransition(() => {
          handleOpenedFilters(label, handleOpenKey, false);
        });
        buttonRef.current?.focus();
      }
    },
    [handleOpenedFilters, label, handleOpenKey],
  );

  const handleToggleOpen = useCallback(() => {
    startTransition(() => {
      handleOpenedFilters(label, handleOpenKey, !isOpen);
    });
  }, [handleOpenedFilters, label, handleOpenKey, isOpen]);

  const selectedOptionData = useMemo(
    () => options.find(option => option.value === selectedOption),
    [options, selectedOption],
  );

  const displayText = selectedOptionData?.label || placeholder || label;

  return (
    <Box ref={dropdownRef}>
      <Button
        ref={buttonRef}
        onClick={handleToggleOpen}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={`${label}-dropdown-options`}
        variant="outlined"
        fullWidth
        sx={{
          justifyContent: 'space-between',
          textAlign: 'left',
          backgroundColor: isSelected ? 'greens.light' : 'white',
          borderWidth: isSelected ? '2px' : '1px',
          borderColor: isSelected ? 'primary.main' : 'neutral.lighter',
          borderRadius: buttonBorderRadius,
          py: buttonPaddingY,
          minHeight: '40px',
          px: 3,
          textTransform: 'none',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'blues.light',
            cursor: 'pointer',
          },
          transition: 'none',
        }}
      >
        <Typography
          sx={{
            color: isSelected ? 'primary.main' : 'text.secondary',
            fontWeight: 500,
            fontSize: '14px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayText}
        </Typography>
        <Box
          sx={{
            color: 'text.secondary',
            pl: '8px',
            pt: '2px',
            flexShrink: 0,
          }}
        >
          <ExpandIcon width={15} />
        </Box>
      </Button>
      <Popper
        open={isOpen}
        anchorEl={buttonRef.current}
        placement="bottom-start"
        popperOptions={{ strategy: 'fixed' }}
        sx={{ zIndex: 200 }}
      >
        <ClickAwayListener onClickAway={() => handleOpenedFilters(label, handleOpenKey, false)}>
          <Paper
            square
            elevation={3}
            sx={{
              minWidth: '200px',
              borderRadius: '8px',
              py: 2,
              my: 2,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1,
                height: 36,
                width: '100%',
                bgcolor: 'white',
              }}
            >
              <Typography sx={{ color: 'text.secondary', fontWeight: 500, fontSize: 14 }}>
                {label}
              </Typography>
            </Box>
            <ListBox
              isOpen={isOpen}
              closeDropdown={() => {
                handleOpenedFilters(label, handleOpenKey, false);
                buttonRef.current?.focus();
              }}
              activeIndex={activeIndex}
              setActiveIndex={setActiveIndex}
              scrollBackToMainBar={scrollBackToMainBar}
              options={options}
              selectedOption={selectedOption}
              onChange={onChange}
              label={label}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
